import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, MapPin, Filter, SlidersHorizontal, X, ChevronLeft, ChevronRight, Building2, ShieldCheck, Star, TrendingUp, LayoutGrid, List } from 'lucide-react';
import Breadcrumb from '../components/common/Breadcrumb';
import SEO from '../components/common/SEO';
import BusinessCard, { BusinessListCard } from '../components/business/BusinessCard';
import Toast from '../components/common/Toast';
import axios from 'axios';

// Skeleton card for loading state — matches new logo-centric card layout
const SkeletonCard = ({ listMode = false }) => (
  listMode ? (
    <div className="bg-white border border-gray-100 rounded-2xl p-4 animate-pulse">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-xl bg-gray-200 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-2/3" />
          <div className="h-3 bg-gray-100 rounded w-1/3" />
          <div className="flex gap-2 pt-1">
            <div className="h-3 bg-gray-200 rounded w-24" />
            <div className="h-3 bg-gray-100 rounded w-16" />
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden animate-pulse">
      <div className="h-16 bg-gray-100" />
      <div className="px-5 -mt-6 pb-0">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-gray-200 flex-shrink-0" />
          <div className="mt-2 flex-1 space-y-1.5">
            <div className="h-3 bg-gray-200 rounded w-2/3" />
            <div className="h-2.5 bg-gray-100 rounded w-1/2" />
          </div>
        </div>
      </div>
      <div className="p-5 space-y-3">
        <div className="h-3 bg-gray-100 rounded w-1/2" />
        <div className="border-t border-gray-100 pt-3 flex justify-between">
          <div className="h-3 bg-gray-200 rounded w-24" />
          <div className="h-3 bg-gray-100 rounded w-16" />
        </div>
      </div>
      <div className="px-5 pb-4">
        <div className="h-3 bg-gray-100 rounded w-20" />
      </div>
    </div>
  )
);

// Active filter chip
const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 bg-sky-50 text-sky-700 border border-sky-200 text-xs font-medium px-3 py-1.5 rounded-full">
    {label}
    <button onClick={onRemove} className="hover:text-sky-900 transition">
      <X size={12} />
    </button>
  </span>
);

const SORT_OPTIONS = [
  { value: 'rating', label: 'Highest Rated', icon: Star },
  { value: 'reviews', label: 'Most Reviewed', icon: TrendingUp },
  { value: 'name', label: 'Name (A–Z)', icon: Building2 },
];

const BusinessesListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get('category') || '';
  const categoryNameFromUrl = searchParams.get('categoryName') || '';

  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [pagination, setPagination] = useState({ offset: 0, limit: 12, total: 0, hasMore: false });

  const [filters, setFilters] = useState({
    search: '',
    location: '',
    category: categoryFromUrl,
    category_name: categoryNameFromUrl,
    sort_by: 'rating',
    verified_only: false,
  });

  const searchTimerRef = useRef(null);
  const cardsContainerRef = useRef(null);

  // Auto-scroll to cards section when category changes or loading finishes
  useEffect(() => {
    if (!loading && cardsContainerRef.current) {
      setTimeout(() => {
        cardsContainerRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [categoryFromUrl, loading]);

  const fetchBusinesses = useCallback(async (offset = 0, currentFilters = filters) => {
    setLoading(true);
    try {
      // Debug: Log what we're sending

      // Build params filtering out empty strings
      const params = new URLSearchParams();
      if (currentFilters.search) params.append('search', currentFilters.search);
      if (currentFilters.location) params.append('location', currentFilters.location);
      if (currentFilters.category) params.append('category', currentFilters.category);
      params.append('sort_by', currentFilters.sort_by);
      params.append('verified_only', currentFilters.verified_only);
      params.append('limit', 12);
      params.append('offset', offset);

      const url = `${import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api'}/businesses?${params}`;

      const response = await axios.get(url);

      if (response.data.status === 'success') {
        setBusinesses(response.data.data);
        setPagination({ ...response.data.pagination, offset });
      }
    } catch (error) {
      console.error('Error fetching businesses:', error);
      setToast({ type: 'error', message: 'Failed to load businesses. Please try again.' });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchBusinesses(0, filters);
  }, [filters.sort_by, filters.verified_only, filters.category]);

  useEffect(() => {
    setFilters((prev) => {
      
      if (prev.category === categoryFromUrl && prev.category_name === categoryNameFromUrl) {
        return prev;
      }

      const updated = {
        ...prev,
        category: categoryFromUrl,
        category_name: categoryNameFromUrl,
      };
      return updated;
    });
  }, [categoryFromUrl, categoryNameFromUrl]);

  // Debounced search + location
  const handleTextFilter = (name, value) => {
    const next = { ...filters, [name]: value };
    setFilters(next);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => fetchBusinesses(0, next), 400);
  };

  const handleSortChange = (value) => setFilters(prev => ({ ...prev, sort_by: value }));
  const handleVerifiedToggle = () => setFilters(prev => ({ ...prev, verified_only: !prev.verified_only }));

  const clearCategoryFilter = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('category');
    nextParams.delete('categoryName');
    setSearchParams(nextParams);
    setFilters((prev) => ({ ...prev, category: '', category_name: '' }));
  };

  const clearAllFilters = () => {
    const reset = { search: '', location: '', category: '', category_name: '', sort_by: 'rating', verified_only: false };
    setFilters(reset);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('category');
    nextParams.delete('categoryName');
    setSearchParams(nextParams);
    fetchBusinesses(0, reset);
  };

  const handlePage = (newOffset) => {
    fetchBusinesses(newOffset);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeFilterCount = [
    filters.search,
    filters.location,
    filters.category,
    filters.verified_only,
    filters.sort_by !== 'rating',
  ].filter(Boolean).length;

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <SEO 
        title="Browse Trusted Local Businesses" 
        description="Search and compare verified local businesses on VMC. Read authentic customer reviews and check quality signals before you decide." 
      />

      {/* ── Hero / Search Bar ─────────────────────────────────── */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 text-white">
        <div className="container-custom pt-10 pb-12">
          <Breadcrumb
            items={[{ label: 'Home', href: '/' }, { label: 'Businesses' }]}
          />
          <div className="mt-6 mb-8">
            <h1 className="text-4xl font-extrabold tracking-tight mb-2">
              Discover Trusted Businesses
            </h1>
            <p className="text-slate-300 text-base max-w-xl">
              Browse genuine reviews from real customers. Find the best services near you.
            </p>
          </div>

          {/* Search Row */}
          <div className="flex flex-col sm:flex-row gap-3 max-w-3xl">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search business name…"
                value={filters.search}
                onChange={e => handleTextFilter('search', e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/10 backdrop-blur border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:bg-white/15 transition text-sm"
              />
            </div>
            <div className="relative sm:w-64">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="City or country…"
                value={filters.location}
                onChange={e => handleTextFilter('location', e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/10 backdrop-blur border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:bg-white/15 transition text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats Bar ─────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container-custom py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-6 text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{pagination.total.toLocaleString()} businesses</span>
            {activeFilterCount > 0 && (
              <span className="text-sky-600 font-medium">{activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active</span>
            )}
          </div>

          {/* Sort + View Controls */}
          <div className="flex items-center gap-3">
            {/* Sort Pills */}
            <div className="hidden md:flex items-center gap-1 bg-gray-100 rounded-xl p-1">
              {SORT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleSortChange(opt.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    filters.sort_by === opt.value
                      ? 'bg-white text-sky-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <opt.icon size={12} />
                  {opt.label}
                </button>
              ))}
            </div>

            {/* View Mode */}
            <div className="hidden sm:flex items-center bg-gray-100 rounded-xl p-1 gap-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-lg transition ${viewMode === 'grid' ? 'bg-white shadow-sm text-sky-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <LayoutGrid size={14} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-lg transition ${viewMode === 'list' ? 'bg-white shadow-sm text-sky-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <List size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Active Filter Chips */}
        {activeFilterCount > 0 && (
          <div className="container-custom pb-3 flex items-center gap-2 flex-wrap">
            {filters.search && (
              <FilterChip label={`"${filters.search}"`} onRemove={() => handleTextFilter('search', '')} />
            )}
            {filters.location && (
              <FilterChip label={`📍 ${filters.location}`} onRemove={() => handleTextFilter('location', '')} />
            )}
            {filters.category && (
              <FilterChip
                label={`Category: ${filters.category_name || 'Selected Category'}`}
                onRemove={clearCategoryFilter}
              />
            )}
            {filters.verified_only && (
              <FilterChip label="✓ Verified only" onRemove={handleVerifiedToggle} />
            )}
            {filters.sort_by !== 'rating' && (
              <FilterChip
                label={`Sort: ${SORT_OPTIONS.find(o => o.value === filters.sort_by)?.label}`}
                onRemove={() => handleSortChange('rating')}
              />
            )}
            <button onClick={clearAllFilters} className="text-xs text-gray-400 hover:text-red-500 transition ml-1 font-medium">
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* ── Layout ────────────────────────────────────────────── */}
      <div className="container-custom py-8">
        <div className={`flex gap-8 ${sidebarOpen ? '' : ''}`}>

          {/* ── Main Content ─────────────────────────────────── */}
          <div className="flex-1 min-w-0" ref={cardsContainerRef}>

            {/* Loading */}
            {loading ? (
              <div className={`grid gap-5 ${
                viewMode === 'list'
                  ? 'grid-cols-1'
                  : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'
              }`}>
                {Array.from({ length: 9 }).map((_, i) => <SkeletonCard key={i} listMode={viewMode === 'list'} />)}
              </div>

            ) : businesses.length === 0 ? (
              /* Empty State */
              <div className="bg-white rounded-2xl border border-gray-100 py-20 text-center shadow-sm">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <Building2 size={28} className="text-gray-400" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">No Businesses Found</h2>
                <p className="text-gray-500 text-sm max-w-xs mx-auto mb-6">
                  Try adjusting your search or filters to find what you're looking for.
                </p>
                <button
                  onClick={clearAllFilters}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-sky-600 text-white text-sm font-semibold rounded-xl hover:bg-sky-700 transition"
                >
                  <X size={14} />
                  Clear All Filters
                </button>
              </div>

            ) : (
              <>
                {/* Count Line */}
                <p className="text-sm text-gray-500 mb-4">
                  Showing <span className="font-semibold text-gray-800">
                    {Math.min(pagination.offset + 1, pagination.total)}–{Math.min(pagination.offset + pagination.limit, pagination.total)}
                  </span> of <span className="font-semibold text-gray-800">{pagination.total.toLocaleString()}</span> businesses
                </p>

                {/* Grid / List */}
                <div className={`grid gap-5 ${
                  viewMode === 'list'
                    ? 'grid-cols-1'
                    : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'
                }`}>
                  {businesses.map(business => (
                    viewMode === 'list'
                      ? <BusinessListCard key={business.id} business={business} />
                      : <BusinessCard key={business.id} business={business} />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.total > pagination.limit && (
                  <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => handlePage(pagination.offset - pagination.limit)}
                      disabled={pagination.offset === 0}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(totalPages, 7) }).map((_, i) => {
                        const page = i + 1;
                        const isActive = page === currentPage;
                        return (
                          <button
                            key={page}
                            onClick={() => handlePage((page - 1) * pagination.limit)}
                            className={`w-9 h-9 rounded-xl text-sm font-semibold transition-all ${
                              isActive
                                ? 'bg-sky-600 text-white shadow-sm shadow-sky-200'
                                : 'text-gray-500 hover:bg-gray-100'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      {totalPages > 7 && <span className="text-gray-400 px-1">…</span>}
                    </div>

                    <button
                      onClick={() => handlePage(pagination.offset + pagination.limit)}
                      disabled={!pagination.hasMore}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-sky-600 rounded-xl hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}

          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      )}
    </div>
  );
};

export default BusinessesListPage;
